import { ChangeEvent, FormEvent, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { referralPortalClient } from "@/services/referralPortalClient";
import { normalizePhoneForStorage } from "@/utils/phone";

import "./referralPortal.css";

type UploadedFileRow = {
  fileId: string;
  fileName: string;
  contentType: string | null;
  size: number;
};

const CUSTOMER_TYPE_OPTIONS = ["Architect", "Builder", "Homeowner", "Developer", "Other"];
const HELP_TYPE_OPTIONS = ["Design", "Inspection", "Site issue", "Quote from plans", "Other"];

export default function ReferralPortalRequestQuotePage() {
  const navigate = useNavigate();
  const session = referralPortalClient.getSession();
  const [form, setForm] = useState({
    clientFirstName: session?.clientName?.split(" ")[0] || "",
    clientLastName: session?.clientName?.split(" ").slice(1).join(" ") || "",
    clientPhone: "",
    clientEmail: session?.email || "",
    projectAddress: "",
    customerType: "",
    helpType: "",
    suburb: "",
    postCode: "",
    council: "",
    notes: "",
  });
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFileRow[]>([]);
  const [status, setStatus] = useState("");
  const [statusTone, setStatusTone] = useState<"info" | "success" | "warning" | "danger">("info");
  const [duplicateSummary, setDuplicateSummary] = useState<string>("");
  const [isCheckingDuplicates, setIsCheckingDuplicates] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  const canSubmit = useMemo(() => {
    return Boolean(
      form.clientFirstName &&
      form.clientPhone &&
      form.clientEmail &&
      form.projectAddress &&
      form.customerType &&
      form.helpType &&
      uploadedFiles.length > 0 &&
      !isSubmitting &&
      !isUploading
    );
  }, [form, uploadedFiles, isSubmitting, isUploading]);

  function updateField(field: keyof typeof form, value: string) {
    setForm((prev) => ({ ...prev, [field]: value }));
  }

  function formatClientPhoneField() {
    setForm((prev) => ({
      ...prev,
      clientPhone: normalizePhoneForStorage(prev.clientPhone) || "",
    }));
  }

  async function handleFilesSelected(event: ChangeEvent<HTMLInputElement>) {
    const fileList = Array.from(event.target.files || []);
    if (fileList.length === 0) {
      return;
    }

    setIsUploading(true);
    setStatus("");
    try {
      const uploaded = [];
      for (const file of fileList) {
        uploaded.push(await referralPortalClient.uploadFile(file));
      }
      setUploadedFiles((prev) => [...prev, ...uploaded]);
      setStatusTone("success");
      setStatus(`${uploaded.length} file${uploaded.length === 1 ? "" : "s"} uploaded successfully.`);
    } catch (error) {
      setStatusTone("danger");
      setStatus((error as Error).message);
    } finally {
      setIsUploading(false);
      event.target.value = "";
    }
  }

  async function handleCheckDuplicates() {
    setIsCheckingDuplicates(true);
    setStatus("");
    try {
      const result = await referralPortalClient.checkDuplicate({
        clientFirstName: form.clientFirstName,
        clientLastName: form.clientLastName,
        clientPhone: form.clientPhone,
        clientEmail: form.clientEmail,
        projectAddress: form.projectAddress,
        customerType: form.customerType,
        helpType: form.helpType,
        suburb: form.suburb,
        postCode: form.postCode ? Number(form.postCode) : null,
        council: form.council,
        notes: form.notes,
      });
      if (result.decision === "allow") {
        setStatusTone("success");
        setDuplicateSummary("No strong duplicates were found.");
      } else if (result.decision === "review") {
        setStatusTone("warning");
        setDuplicateSummary(`Possible duplicate found. ${result.matches.length} request(s) need manual review.`);
      } else {
        setStatusTone("danger");
        setDuplicateSummary("A matching request already exists and this submission would be blocked.");
      }
    } catch (error) {
      setStatusTone("danger");
      setDuplicateSummary((error as Error).message);
    } finally {
      setIsCheckingDuplicates(false);
    }
  }

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setIsSubmitting(true);
    setStatus("");
    try {
      const created = await referralPortalClient.createQuoteRequest({
        clientFirstName: form.clientFirstName,
        clientLastName: form.clientLastName || undefined,
        clientPhone: form.clientPhone,
        clientEmail: form.clientEmail,
        projectAddress: form.projectAddress,
        customerType: form.customerType,
        helpType: form.helpType,
        suburb: form.suburb || undefined,
        postCode: form.postCode ? Number(form.postCode) : null,
        council: form.council || undefined,
        notes: form.notes || undefined,
        uploadedFiles,
      });
      setStatusTone(created.duplicateDecision === "review" ? "warning" : "success");
      setStatus(`Request ${created.referralCode} submitted successfully.`);
      navigate("/refer/dashboard");
    } catch (error) {
      setStatusTone("danger");
      setStatus((error as Error).message);
    } finally {
      setIsSubmitting(false);
    }
  }

  if (session?.isAdmin) {
    return (
      <div className="referral-portal">
        <div className="referral-portal__shell">
          <div className="referral-portal__layout">
            <section className="referral-portal__panel">
              <div className="referral-portal__eyebrow">Referral Admin</div>
              <h1>Admin users do not submit quote requests here</h1>
              <p>
                This route is reserved for client submissions. Your admin access is focused on the referral
                program dashboard so you can review intake, duplicates, quotations, and projects without changing the portal style.
              </p>
              <div className="referral-portal__panel-actions">
                <button className="referral-portal__button" onClick={() => navigate("/refer/dashboard")}>
                  Open admin dashboard
                </button>
                <button className="referral-portal__button--ghost" onClick={() => navigate("/refer")}>
                  Back to portal home
                </button>
              </div>
            </section>

            <aside className="referral-portal__card">
              <div className="referral-portal__status referral-portal__status--info">
                Signed in as {session.email}
              </div>
            </aside>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="referral-portal">
      <div className="referral-portal__shell">
        <div className="referral-portal__layout">
          <section className="referral-portal__panel">
            <div className="referral-portal__eyebrow">Request Quote</div>
            <h1>Submit a new quotation request</h1>
            <p>
              Drawings are required for the first portal rollout. Upload at least one PDF, PNG, JPEG, HEIC, or HEIF,
              then run the duplicate check before final submission.
            </p>
            <div className="referral-portal__upload-list">
              {uploadedFiles.length === 0 ? (
                <div className="referral-portal__status referral-portal__status--info">No drawings uploaded yet.</div>
              ) : (
                uploadedFiles.map((file) => (
                  <div key={file.fileId} className="referral-portal__upload-item">
                    <span>{file.fileName}</span>
                    <span>{Math.max(1, Math.round(file.size / 1024))} KB</span>
                  </div>
                ))
              )}
            </div>
          </section>

          <aside className="referral-portal__card">
            <form className="referral-portal__form" onSubmit={handleSubmit}>
              <div className="referral-portal__form-grid">
                <div className="referral-portal__field">
                  <label htmlFor="rq-firstName">Client first name</label>
                  <input id="rq-firstName" value={form.clientFirstName} onChange={(e) => updateField("clientFirstName", e.target.value)} required />
                </div>
                <div className="referral-portal__field">
                  <label htmlFor="rq-lastName">Client last name</label>
                  <input id="rq-lastName" value={form.clientLastName} onChange={(e) => updateField("clientLastName", e.target.value)} />
                </div>
                <div className="referral-portal__field">
                  <label htmlFor="rq-phone">Phone</label>
                  <input id="rq-phone" type="tel" value={form.clientPhone} onChange={(e) => updateField("clientPhone", e.target.value)} onBlur={formatClientPhoneField} required />
                </div>
                <div className="referral-portal__field">
                  <label htmlFor="rq-email">Email</label>
                  <input id="rq-email" type="email" value={form.clientEmail} onChange={(e) => updateField("clientEmail", e.target.value)} required />
                </div>
                <div className="referral-portal__field">
                  <label htmlFor="rq-customerType">I am a</label>
                  <select id="rq-customerType" value={form.customerType} onChange={(e) => updateField("customerType", e.target.value)} required>
                    <option value="">Select role</option>
                    {CUSTOMER_TYPE_OPTIONS.map((option) => (
                      <option key={option} value={option}>{option}</option>
                    ))}
                  </select>
                </div>
                <div className="referral-portal__field">
                  <label htmlFor="rq-helpType">Need help with</label>
                  <select id="rq-helpType" value={form.helpType} onChange={(e) => updateField("helpType", e.target.value)} required>
                    <option value="">Select help type</option>
                    {HELP_TYPE_OPTIONS.map((option) => (
                      <option key={option} value={option}>{option}</option>
                    ))}
                  </select>
                </div>
                <div className="referral-portal__field" style={{ gridColumn: "1 / -1" }}>
                  <label htmlFor="rq-address">Project address</label>
                  <input id="rq-address" value={form.projectAddress} onChange={(e) => updateField("projectAddress", e.target.value)} required />
                </div>
                <div className="referral-portal__field">
                  <label htmlFor="rq-suburb">Suburb</label>
                  <input id="rq-suburb" value={form.suburb} onChange={(e) => updateField("suburb", e.target.value)} />
                </div>
                <div className="referral-portal__field">
                  <label htmlFor="rq-postCode">Postcode</label>
                  <input id="rq-postCode" inputMode="numeric" value={form.postCode} onChange={(e) => updateField("postCode", e.target.value)} />
                </div>
                <div className="referral-portal__field" style={{ gridColumn: "1 / -1" }}>
                  <label htmlFor="rq-council">Council</label>
                  <input id="rq-council" value={form.council} onChange={(e) => updateField("council", e.target.value)} />
                </div>
              </div>

              <div className="referral-portal__field">
                <label htmlFor="rq-notes">Notes</label>
                <textarea id="rq-notes" value={form.notes} onChange={(e) => updateField("notes", e.target.value)} />
              </div>

              <div className="referral-portal__field">
                <label htmlFor="rq-files">Architectural drawings</label>
                <input id="rq-files" type="file" accept=".pdf,.png,.jpg,.jpeg,.heic,.heif,application/pdf,image/png,image/jpeg,image/heic,image/heif,image/heic-sequence,image/heif-sequence" multiple onChange={handleFilesSelected} />
              </div>

              {duplicateSummary ? (
                <div className={`referral-portal__status referral-portal__status--${statusTone}`}>{duplicateSummary}</div>
              ) : null}
              {status ? (
                <div className={`referral-portal__status referral-portal__status--${statusTone}`}>{status}</div>
              ) : null}

              <div className="referral-portal__actions">
                <button className="referral-portal__button--ghost" type="button" onClick={handleCheckDuplicates} disabled={isCheckingDuplicates || isUploading}>
                  {isCheckingDuplicates ? "Checking..." : "Check duplicates"}
                </button>
                <button className="referral-portal__button" type="submit" disabled={!canSubmit}>
                  {isSubmitting ? "Submitting..." : "Submit request"}
                </button>
              </div>
            </form>
          </aside>
        </div>
      </div>
    </div>
  );
}
